import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, FileText, Upload, Loader2, Download } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const inputCls =
  "w-full bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#234333] focus:border-[#234333]";

function academicYearOptions(yearOfEstablishment) {
  const start = Math.max(2000, Number(yearOfEstablishment) || new Date().getFullYear() - 20);
  const end = new Date().getFullYear() + 1; // include current upcoming AY
  const list = [];
  for (let y = end; y >= start; y--) {
    list.push(`${y}-${String((y + 1) % 100).padStart(2, "0")}`);
  }
  return list;
}

export default function FraFfc() {
  const { role } = useAuth();
  const writable = role === "institute_admin";
  const [items, setItems] = useState([]);
  const [yearOfEst, setYearOfEst] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ academic_year: "", title: "", pdf_url: "", pdf_public_id: "", order: 0, status: "Active" });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get("/institute/fra-ffc-documents"),
        api.get("/institute/info"),
      ]);
      setItems(a.data);
      setYearOfEst(b.data?.year_of_establishment || null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchAll();
  }, []);

  const years = useMemo(() => academicYearOptions(yearOfEst), [yearOfEst]);

  const openCreate = () => {
    setEditing(null);
    setForm({ academic_year: years[0] || "", title: "", pdf_url: "", pdf_public_id: "", order: 0, status: "Active" });
    setOpen(true);
  };
  const openEdit = (it) => {
    setEditing(it);
    setForm({ ...it });
    setOpen(true);
  };

  const uploadPdf = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf") {
      toast.error("Only PDF files are allowed");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("PDF must be 5 MB or smaller");
      return;
    }
    const fd = new FormData();
    fd.append("kind", "inst_fra_ffc_doc");
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set("pdf_url", data.url);
      set("pdf_public_id", data.public_id || "");
      toast.success("PDF uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!form.academic_year) return toast.error("Select an academic year");
    if (!form.title.trim()) return toast.error("Title is required");
    if (!form.pdf_url) return toast.error("Upload a PDF first");
    setSaving(true);
    try {
      if (editing) {
        const { data } = await api.put(`/institute/fra-ffc-documents/${editing.id}`, form);
        setItems((arr) => arr.map((x) => (x.id === data.id ? data : x)));
        toast.success("Updated");
      } else {
        const { data } = await api.post("/institute/fra-ffc-documents", form);
        setItems((arr) => [data, ...arr]);
        toast.success("Added");
      }
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/institute/fra-ffc-documents/${confirmDelete.id}`);
      setItems((arr) => arr.filter((x) => x.id !== confirmDelete.id));
      toast.success("Deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    } finally {
      setConfirmDelete(null);
    }
  };

  return (
    <div className="p-8 md:p-12 max-w-7xl mx-auto" data-testid="fra-ffc-page">
      <div className="flex items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#6b655b]">Compliance</div>
          <h1 className="mt-2 font-serif text-4xl text-[#1c1a17]">FRA / FFC</h1>
          <p className="mt-2 text-sm text-[#6b655b]">
            Fees Regulating Authority & Fees Fixation Committee filings. Uploaded PDFs are
            published on your institute&apos;s public website.
          </p>
        </div>
        {writable ? (
          <button
            onClick={openCreate}
            data-testid="ff-add-button"
            className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-5 py-2.5 rounded-sm"
          >
            <Plus size={14} strokeWidth={1.5} /> Add Document
          </button>
        ) : null}
      </div>

      {loading ? (
        <div className="text-sm text-[#6b655b]">Loading…</div>
      ) : items.length === 0 ? (
        <div data-testid="ff-empty" className="border border-dashed border-[#e5e0d8] rounded-sm p-12 text-center bg-white">
          <FileText size={28} className="mx-auto text-[#6b655b] mb-3" />
          <p className="text-sm text-[#6b655b]">No FRA/FFC documents yet.</p>
        </div>
      ) : (
        <div className="bg-white border border-[#e5e0d8] rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#f9f8f6]">
              <tr>
                {["Academic Year", "Title", "Status", "Download", ""].map((h) => (
                  <th key={h} className="px-4 py-3 text-[10px] tracking-wide uppercase text-[#6b655b] font-semibold text-left">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#efece6]">
              {items.map((it) => (
                <tr key={it.id} data-testid={`ff-row-${it.id}`} className="hover:bg-[#f9f8f6]/60">
                  <td className="px-4 py-3 font-mono text-[#1c1a17]">{it.academic_year}</td>
                  <td className="px-4 py-3 text-[#1c1a17]">{it.title}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 text-[10px] tracking-wide uppercase border rounded-sm ${
                      it.status === "Active"
                        ? "bg-[#234333]/10 text-[#234333] border-[#234333]/30"
                        : "bg-[#d4af37]/10 text-[#8a6d1d] border-[#d4af37]/30"
                    }`}>
                      {it.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {it.pdf_url ? (
                      <a href={it.pdf_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#234333] hover:underline text-xs">
                        <Download size={12} /> PDF
                      </a>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {writable ? (
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => openEdit(it)} data-testid={`ff-edit-${it.id}`} className="p-2 text-[#234333] hover:bg-[#efece6] rounded-sm">
                          <Pencil size={14} strokeWidth={1.5} />
                        </button>
                        <button onClick={() => setConfirmDelete(it)} data-testid={`ff-delete-${it.id}`} className="p-2 text-[#9e2a2b] hover:bg-[#9e2a2b]/5 rounded-sm">
                          <Trash2 size={14} strokeWidth={1.5} />
                        </button>
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="ff-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Document" : "Add FRA/FFC Document"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-4 py-2">
            <label className="block">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">Academic Year</div>
              <select data-testid="ff-year" className={inputCls} value={form.academic_year} onChange={(e) => set("academic_year", e.target.value)}>
                <option value="">— Select —</option>
                {years.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">Title</div>
              <input data-testid="ff-title" className={inputCls} value={form.title} onChange={(e) => set("title", e.target.value)} maxLength={200} placeholder="e.g. FRA Order — Tuition Fees 2024-25" />
            </label>
            <label className="block">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">PDF · max 5 MB</div>
              <div className="flex items-center gap-3">
                <label className="inline-flex items-center gap-2 px-3 py-2 border border-[#e5e0d8] rounded-sm cursor-pointer text-sm hover:bg-[#efece6]">
                  {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                  {uploading ? "Uploading…" : form.pdf_url ? "Replace PDF" : "Choose PDF"}
                  <input type="file" accept="application/pdf" className="hidden" data-testid="ff-file" onChange={(e) => uploadPdf(e.target.files?.[0])} />
                </label>
                {form.pdf_url ? (
                  <a href={form.pdf_url} target="_blank" rel="noreferrer" data-testid="ff-preview-link" className="text-xs text-[#234333] hover:underline">
                    Preview uploaded PDF →
                  </a>
                ) : null}
              </div>
            </label>
            <div className="grid grid-cols-2 gap-4">
              <label className="block">
                <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">Order</div>
                <input type="number" data-testid="ff-order" className={inputCls} value={form.order} onChange={(e) => set("order", Number(e.target.value) || 0)} />
              </label>
              <label className="block">
                <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">Status</div>
                <select data-testid="ff-status" className={inputCls} value={form.status} onChange={(e) => set("status", e.target.value)}>
                  <option value="Active">Active</option>
                  <option value="Archived">Archived</option>
                </select>
              </label>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-5 py-2 text-sm text-[#6b655b] hover:bg-[#efece6] rounded-sm">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="ff-save" className="bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-5 py-2 rounded-sm disabled:opacity-50">
              {saving ? "Saving…" : editing ? "Update" : "Add"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete document?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete?.academic_year} · {confirmDelete?.title}. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={del} className="bg-[#9e2a2b] hover:bg-[#7e2122] text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
